import React, { useState } from "react";
import CustomModal from "../Common/CustomModal";
import { Box, Grid } from "@mui/material";
import { CustomInput } from "../Common/CustomInput";
import { colors } from "../../theme/theme";
import {
  ConfirmationNumberOutlined,
  LocationOnOutlined,
  TagSharp,
  TitleSharp,
} from "@mui/icons-material";
import CustomDatePicker from "../Common/CustomDatePicker";
import CustomDropdown from "../Common/CustomDropdown";
import CustomTagInput from "../Common/CustomTagInput";
import { CustomBtn } from "../Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import { useSelector } from "react-redux";
import ImageUploadInput from "../Common/ImageUploadInput";

const CreateEventModal = ({
  open,
  onClose,
  setLoading,
  setErrorState,
  setSuccess,
}) => {
  const { userData } = useSelector((state) => state.user);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(null);
  const [locationType, setLocationType] = useState("");
  const [location, setLocation] = useState("");
  const [ticketing, setTicketing] = useState("");
  const [price, setPrice] = useState("");
  const [interests, setInterests] = useState([]);
  const [image, setImage] = useState(null);

  const handlePublish = async () => {
    setErrorState({
      error: false,
      errorMsg: "",
    });

    if (!title || !description || !date || !locationType || !ticketing) {
      setErrorState({
        error: true,
        errorMsg: "Please fill all the fields",
      });
      return;
    }

    if (ticketing === "paid" && !price) {
      setErrorState({
        error: true,
        errorMsg: "Please enter ticket price",
      });
      return;
    }

    try {
      setLoading(true);

      onClose();
      const response = await axiosInstance.post("/event/createEvent", {
        event: {
          title: title,
          description: description,
          image_url: image,
          created_by: userData?.id,
          event_date: date,
          location: location,
          ticketing_option: ticketing,
          interest_tags: interests,
          status: "pending",
          event_type: locationType,
          ticket_price: ticketing === "paid" ? price : 0,
        },
      });

      if (response?.status === 200) {
        setErrorState({
          error: false,
          errorMsg: "",
        });
        setTitle("");
        setDescription("");
        setDate(null);
        setLocation("");
        setTicketing("");
        setInterests([]);
        setSuccess(true);
        setImage(null);
      }
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error?.data?.response?.message || "Failed to create event",
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} title="Create Event">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "50%",
          margin: "0 auto",
        }}
      >
        <CustomInput
          label={"Event Title"}
          value={title}
          handleChange={(e) => setTitle(e.target.value)}
          placeholder={"Enter event title"}
          Icon={TitleSharp}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
        />
        <CustomInput
          label={"Event Description"}
          value={description}
          handleChange={(e) => setDescription(e.target.value)}
          placeholder={"Enter event description"}
          noIcon
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
          textArea
        />
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <CustomDatePicker
              label={"Event Date"}
              value={date}
              setValue={setDate}
              placeholder={"Select event date"}
              width={"100%"}
              mT={2}
              borderColor={colors.secondary}
              color={colors.primary}
              iconColor={"rgba(25,34,52,0.5)"}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <CustomDropdown
              label={"Event Location Type"}
              placeholder={"Select location type"}
              value={locationType}
              setValue={setLocationType}
              data={[
                { label: "Virtual", value: "Virtual" },
                { label: "Physical", value: "Physical" },
              ]}
              width={"100%"}
              mT={2}
              borderColor={colors.secondary}
              color={colors.primary}
              Icon={ConfirmationNumberOutlined}
            />
          </Grid>
        </Grid>
        <CustomInput
          label={"Event Location (physical only)"}
          value={location}
          handleChange={(e) => setLocation(e.target.value)}
          placeholder={"Enter event location (physical only)"}
          Icon={LocationOnOutlined}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
        />
        <CustomDropdown
          label={"Ticketing Options"}
          placeholder={"Select ticketing option"}
          value={ticketing}
          setValue={setTicketing}
          data={[
            { label: "Free", value: "free" },
            { label: "Paid", value: "paid" },
          ]}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
          color={colors.primary}
          Icon={ConfirmationNumberOutlined}
        />
        {ticketing === "paid" && (
          <CustomInput
            label={"Ticket Price"}
            value={price}
            handleChange={(e) => setPrice(e.target.value)}
            placeholder={"Enter ticket price"}
            noIcon
            width={"100%"}
            mT={2}
            borderColor={colors.secondary}
            type={"number"}
          />
        )}
        <CustomTagInput
          label={"Interests"}
          hashTags={interests}
          setHashTags={setInterests}
          placeholder={"Add interests"}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
          color={colors.primary}
          Icon={TagSharp}
        />

        <ImageUploadInput image={image} setImage={setImage} width={"90%"} />

        <CustomBtn
          text={"Publish"}
          width={"100%"}
          height={"50px"}
          mt={2}
          mb={2}
          onClick={handlePublish}
        />
      </Box>
    </CustomModal>
  );
};

export default CreateEventModal;
